import { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${window.location.origin}/api`;

export default function FavoriteButton({ businessId, size = 'md', showLabel = false, onToggle }) {
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const checkFavorite = async () => {
      try {
        const response = await fetch(`${API}/favorites/${businessId}/check`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsFavorite(data.is_favorite);
        }
      } catch (error) {
        // User might not be logged in
        console.log('Could not check favorite status');
      } finally {
        setLoading(false);
      }
    };

    checkFavorite();
  }, [businessId]);

  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setAnimating(true);
    
    try {
      if (isFavorite) {
        // Remove from favorites
        const response = await fetch(`${API}/favorites/${businessId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        if (response.ok) {
          setIsFavorite(false);
          toast.success('Removed from favorites');
          onToggle?.(false);
        } else if (response.status === 401) {
          toast.error('Please sign in to save favorites');
        }
      } else {
        // Add to favorites
        const response = await fetch(`${API}/favorites/${businessId}`, {
          method: 'POST',
          credentials: 'include'
        });
        
        if (response.ok) {
          setIsFavorite(true);
          toast.success('Added to favorites');
          onToggle?.(true);
        } else if (response.status === 401) {
          toast.error('Please sign in to save favorites');
        }
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setTimeout(() => setAnimating(false), 300);
    }
  };

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  if (loading) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-black/30 backdrop-blur-sm animate-pulse`} />
    );
  }

  return (
    <motion.button
      onClick={handleToggle}
      whileTap={{ scale: 0.9 }}
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center transition-all duration-200 ${
        isFavorite
          ? 'bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30'
          : 'bg-black/50 backdrop-blur-sm border border-white/10 text-white/70 hover:text-white hover:border-white/30'
      } ${showLabel ? 'px-4 w-auto gap-2' : ''}`}
      data-testid={`favorite-button-${businessId}`}
    >
      <motion.div
        animate={animating ? { scale: [1, 1.3, 1] } : {}}
        transition={{ duration: 0.3 }}
      >
        <Heart
          className={`${iconSizes[size]} ${isFavorite ? 'fill-red-400' : ''}`}
        />
      </motion.div>
      {showLabel && (
        <span className="text-sm font-medium">
          {isFavorite ? 'Saved' : 'Save'}
        </span>
      )}
    </motion.button>
  );
}
